import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Search, X } from "lucide-react";
import { api } from "@/api";
import { useFavorites } from "@/hooks/useFavorites";
import { StickerGrid } from "@/components/StickerGrid";
import { usePagedDetail, LoadMore } from "@/lib/paged";

const normalize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function Categories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const term = normalize(query.trim());
  const shown = term ? cats.filter((c) => normalize(c.name).includes(term)) : cats;

  useEffect(() => {
    api.get("/categories").then((res) => setCats(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="animate-spin text-[#C58B90]" size={32} />
      </div>
    );

  return (
    <div className="space-y-6">
      <h1 className="font-serif text-3xl">Categorias</h1>
      <div className="relative">
        <Search size={19} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
        <input
          data-testid="categories-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar categoria (ex: café, fitness)…"
          className="w-full py-3.5 pl-12 pr-11 rounded-full bg-white border border-[#E8CFD1] outline-none focus:border-[#C58B90] focus:ring-2 focus:ring-[#F2E6E7] transition-[border-color,box-shadow] text-sm"
        />
        {query && (
          <button data-testid="categories-search-clear" onClick={() => setQuery("")} aria-label="Limpar busca" className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-[#7A7571] hover:bg-[#F7EBEC] transition-colors">
            <X size={16} />
          </button>
        )}
      </div>
      {shown.length === 0 && (
        <p data-testid="categories-search-empty" className="py-10 text-center text-sm text-[#7A7571]">Nenhuma categoria encontrada para "{query}".</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {shown.map((c, i) => (
          <button
            key={c.id}
            data-testid={`category-card-${c.id}`}
            onClick={() => navigate(`/categorias/${c.id}`)}
            className="animate-float-up text-left bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft transition-transform duration-300 hover:-translate-y-1"
            style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
          >
            <div
              className="h-16 w-16 rounded-2xl flex items-center justify-center text-3xl mb-3"
              style={{ background: "linear-gradient(135deg,#F2E6E7,#E8CFD1)" }}
            >
              {c.emoji}
            </div>
            <p className="font-serif text-lg text-[#2C2A29] leading-tight">{c.name}</p>
            <p className="text-xs text-[#7A7571] mt-1">{c.sticker_count} stickers</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function CategoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: cat, loading, hasMore, loadingMore, loadMore } = usePagedDetail(`/categories/${id}`);
  const { favIds, onToggleFav } = useFavorites();

  if (loading)
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="animate-spin text-[#C58B90]" size={32} />
      </div>
    );
  if (!cat) return <p className="py-16 text-center text-[#7A7571]">Categoria não encontrada.</p>;

  return (
    <div className="space-y-6">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#C58B90] font-medium">
        <ArrowLeft size={17} /> Voltar
      </button>
      <div>
        <h1 className="font-serif text-3xl">
          {cat.emoji} {cat.name}
        </h1>
        <p className="text-sm text-[#7A7571] mt-1">{cat.sticker_count} stickers</p>
      </div>
      <StickerGrid stickers={cat.stickers} favIds={favIds} onToggleFav={onToggleFav} />
      <LoadMore hasMore={hasMore} loading={loadingMore} onMore={loadMore} />
    </div>
  );
}
